/**
 * ZeoPage - neue Vollbild-ZEO-Arbeitsoberflaeche (/zeo).
 *
 * Scope wird AUSSCHLIESSLICH beim Oeffnen aus den URL-Query-Params gelesen
 * und bleibt fuer die gesamte Seitensitzung fest:
 *   /zeo?scope=client&klient_id=<id>   -- harte Klientenbindung
 *   /zeo?scope=office                   -- Buero-/allgemeiner Kontext
 *
 * Wiederverwendet VOLLSTAENDIG die bestehende, bereits funktionierende
 * Persistenz (assistent_sessions/assistent_messages) und den bestehenden
 * Mittwald/Qwen-Pfad (POST /api/assistent/chat) -- keine neue KI-Logik,
 * keine neue Collection, keine Aenderung am Cross-Context-Schutz in
 * assistent_routes.py::chat() (der bleibt server-seitig vollstaendig
 * bestehen und wird hier nur respektiert, nie umgangen).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { betreuungAPI } from '@/api/betreuungAPI';
import ZeoSidebar from './ZeoSidebar';
import ZeoScopeHeader from './ZeoScopeHeader';
import ZeoConversation from './ZeoConversation';
import ZeoComposer from './ZeoComposer';
import { dateienAusDrop, hatDateien } from './zeoAnhang';
import ZeoContextBar from './ZeoContextBar';
import ZeoWorkspaceDialog from './ZeoWorkspaceDialog';
import { scopeAusParams } from './zeoScope';
import { leererKontext, sessionStartPayload, entitiesPayload, scopeParamsFuer, istOpenTask, oeffneAufgabeFenster } from './zeoWorkspace';
import SammelErledigenDialog from '@/components/aufgaben/SammelErledigenDialog';
import ZusammenfuehrenDialog from '@/components/aufgaben/ZusammenfuehrenDialog';
import { nur } from '@/lib/aufgabenAuswahl';
import { aufgabenKontextAbsicht } from './zeoAufgabenKontext';

export default function ZeoPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { scope, kontext_typ, kontext_id } = scopeAusParams(searchParams);

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [klientNamenCache, setKlientNamenCache] = useState({});
  const [aktuellerKlientName, setAktuellerKlientName] = useState(null);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const composerRef = useRef(null);
  const [dateiUeberFenster, setDateiUeberFenster] = useState(false);
  const [loading, setLoading] = useState(false);
  const pollTimerRef = useRef(null);

  // /zeo Arbeitsoberflaeche (07.10.2026): persistenter Chat-Fokus. `kontext` = Anzeigenamen (nie IDs);
  // `kontextIds` = technische Handles (nur fuer Dialoge/Aufrufe, werden nie angezeigt).
  const [kontext, setKontext] = useState(leererKontext());
  const [kontextIds, setKontextIds] = useState({ klient_id: null, task_id: null, document_id: null, vorgang_id: null });
  const [dialog, setDialog] = useState(null); // null | 'neu' | 'klient' | 'aufgabe' | 'neue-aufgabe'

  // ZEO interaktive Antworten (06.10.2026): Reply-Ziel + ausgewaehlte
  // Dokumente leben hier (Seiten-State, nicht pro Komponente) -- gehen beim
  // Senden an den Composer/Backend, werden danach zurueckgesetzt.
  const [replyTarget, setReplyTarget] = useState(null);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  // Aufgabenverwaltung 2.0 im großen ZEO (27.10.2026): Auswahl aus den Aufgabenlisten der Antworten (stabile IDs, über Filter/Nachrichten erhalten).
  const [aufgabenAuswahl, setAufgabenAuswahl] = useState(new Map());
  const [auswahlAlsKontext, setAuswahlAlsKontext] = useState(true);
  const [mergeIds, setMergeIds] = useState(null);
  const [erledigenIds, setErledigenIds] = useState(null);
  const [aufgabenStand, setAufgabenStand] = useState(0);   // erhöht nach Erledigen/Zusammenführen -> Listen laden den aktuellen Status

  const ladeSessions = useCallback(async () => {
    try {
      const liste = await betreuungAPI.listAssistentSessions();
      setSessions(Array.isArray(liste) ? liste : []);

      const unbekannteIds = new Set();
      (Array.isArray(liste) ? liste : []).forEach((s) => {
        if (s.kontext_typ === 'betreuungsakte' && s.kontext_id) unbekannteIds.add(s.kontext_id);
      });
      for (const id of unbekannteIds) {
        if (klientNamenCache[id]) continue;
        try {
          const k = await betreuungAPI.getKlient(id);
          const name = `${k.vorname || ''} ${k.nachname || ''}`.trim();
          if (name) setKlientNamenCache((prev) => ({ ...prev, [id]: name }));
        } catch { /* Klient evtl. geloescht -- Badge bleibt generisch */ }
      }
    } catch { /* Sidebar bleibt leer, kein Blocker fuer den Chat selbst */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { ladeSessions(); }, [ladeSessions]);

  // Klientenname fuer den aktuell gebundenen Scope separat laden (auch wenn
  // es noch keine einzige Session dazu gibt).
  useEffect(() => {
    let abgebrochen = false;
    if (scope === 'client' && kontext_id) {
      if (klientNamenCache[kontext_id]) {
        setAktuellerKlientName(klientNamenCache[kontext_id]);
        return;
      }
      betreuungAPI.getKlient(kontext_id).then((k) => {
        if (abgebrochen) return;
        const name = `${k.vorname || ''} ${k.nachname || ''}`.trim();
        setAktuellerKlientName(name || null);
        if (name) setKlientNamenCache((prev) => ({ ...prev, [kontext_id]: name }));
      }).catch(() => { if (!abgebrochen) setAktuellerKlientName(null); });
    } else {
      setAktuellerKlientName(null);
    }
    return () => { abgebrochen = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, kontext_id]);

  useEffect(() => () => { if (pollTimerRef.current) clearTimeout(pollTimerRef.current); }, []);

  const ladeKontext = useCallback(async (sessionId) => {
    if (!sessionId) { setKontext(leererKontext()); setKontextIds({ klient_id: null, task_id: null, document_id: null, vorgang_id: null }); return; }
    try {
      const r = await betreuungAPI.zeoWorkspaceKontext(sessionId);
      setKontext(r.kontext || leererKontext());
      setKontextIds({ klient_id: r.klient_id, task_id: r.task_id, document_id: r.document_id, vorgang_id: r.vorgang_id });
    } catch { /* Context Bar bleibt auf letztem Stand */ }
  }, []);

  // "Neuer Chat" oeffnet die Auswahl (Buero allgemein ODER Klient, optional Aufgabe).
  const handleNewChat = useCallback(() => setDialog('neu'), []);

  // Karte "Aufgabe oeffnen": oeffnet die kanonische Maske UND bindet die Aufgabe als aktive Aufgabe des Chats (Folgeanweisungen wie "Trag dort ein ..." meinen sie).
  const handleAufgabeOeffnen = useCallback(async (ref) => {
    if (!ref?.id) return;
    oeffneAufgabeFenster(ref.id);
    if (activeSessionId) {
      try {
        await betreuungAPI.zeoWorkspaceEntities(activeSessionId, { task_id: ref.id });
        ladeKontext(activeSessionId);
      } catch { /* Bindung nicht moeglich (z.B. Aufgabe eines anderen Klienten) -- Maske bleibt geoeffnet */ }
    }
  }, [activeSessionId, ladeKontext]);

  // Chat umbenennen (persistent; ein manueller Titel wird serverseitig nie automatisch ueberschrieben).
  const handleRename = useCallback(async (sessionId, titel) => {
    try {
      const r = await betreuungAPI.zeoWorkspaceTitel(sessionId, titel);
      setSessions((prev) => prev.map((s) => (s.id === sessionId ? { ...s, titel: r.titel } : s)));
    } catch { /* Titel bleibt unveraendert */ }
  }, []);
  // Suche in Chatinhalt (serverseitig; mit Klienten-Scope strikt nur Chats dieses Klienten).
  const handleChatSuche = useCallback(async (q, klientId) => {
    try {
      return (await betreuungAPI.zeoWorkspaceSessionsSuche(q, klientId)).treffer || [];
    } catch { return []; }
  }, []);

  const handleStartChat = useCallback(async (auswahl) => {
    try {
      const r = await betreuungAPI.zeoWorkspaceSessionErstellen(sessionStartPayload(auswahl));
      // Fail closed: nichts vom bisherigen Chat/Klienten (Nachrichten, Karten, Fokus, Auswahl) darf ueberleben.
      setActiveSessionId(r.session_id);
      setMessages([]);
      setReplyTarget(null);
      setSelectedDocuments([]);
      setKontext(r.kontext || leererKontext());
      setKontextIds({ klient_id: auswahl.klientId || null, task_id: auswahl.taskId || null, document_id: null, vorgang_id: auswahl.vorgangId || null });
      setSearchParams(scopeParamsFuer(auswahl.klientId));
      setDialog(null);
      await ladeKontext(r.session_id);
      ladeSessions();
    } catch (e) {
      setMessages([{ id: `err-${Date.now()}`, rolle: 'assistent', text: `⚠️ Chat konnte nicht gestartet werden: ${e.message}`, timestamp: new Date().toISOString() }]);
      setDialog(null);
    }
  }, [ladeKontext, ladeSessions, setSearchParams]);

  const handleAufgabeGewaehlt = useCallback(async (taskId, vorgangId) => {
    if (!activeSessionId) return;
    try {
      await betreuungAPI.zeoWorkspaceEntities(activeSessionId, entitiesPayload({ taskId, vorgangId }));
    } catch { /* abgelehnt (z.B. fremde Aufgabe) -- Kontext bleibt unveraendert */ }
    setDialog(null);
    await ladeKontext(activeSessionId);
  }, [activeSessionId, ladeKontext]);

  const handleAufgabeAngelegt = useCallback(async () => {
    setDialog(null);
    await ladeKontext(activeSessionId);   // der Server hat die Aufgabe bereits als aktive Aufgabe gesetzt
  }, [activeSessionId, ladeKontext]);

  // Dokumentkarte geoeffnet/verwendet -> aktives Dokument des Chats (kein Qwen-Aufruf).
  const handleAktivesDokument = useCallback(async (dokumentRef) => {
    if (!activeSessionId || !dokumentRef?.id) return;
    try {
      await betreuungAPI.zeoWorkspaceEntities(activeSessionId, { document_id: dokumentRef.id });
      await ladeKontext(activeSessionId);
    } catch { /* Dokument gehoert nicht zum Klienten -- nicht binden */ }
  }, [activeSessionId, ladeKontext]);

  const handleReply = useCallback((message, block) => {
    const basisText = block ? block.display_text : message.text;
    setReplyTarget({
      message_id: message.id,
      block_id: block ? block.block_id : null,
      ausschnitt: (basisText || '').slice(0, 80),
    });
  }, []);

  const handleToggleDocument = useCallback((dokumentRef) => {
    setSelectedDocuments((prev) => {
      if (prev.some((d) => d.id === dokumentRef.id)) {
        return prev.filter((d) => d.id !== dokumentRef.id);
      }
      return [...prev, dokumentRef];
    });
  }, []);

  const handleRemoveSelectedDocument = useCallback((dokumentId) => {
    setSelectedDocuments((prev) => prev.filter((d) => d.id !== dokumentId));
  }, []);

  const handleSelectSession = useCallback(async (session) => {
    setActiveSessionId(session.id);
    // Ein bestehender Chat behaelt SEINEN EIGENEN Scope -- die URL wird an
    // den Scope DIESES Chats angepasst, niemals umgekehrt (kein stiller
    // Scope-Wechsel eines Chats selbst).
    if (session.kontext_typ === 'betreuungsakte' && session.kontext_id) {
      setSearchParams({ scope: 'client', klient_id: session.kontext_id });
    } else {
      setSearchParams({ scope: 'office' });
    }
    try {
      const voll = await betreuungAPI.getAssistentSession(session.id);
      setMessages(voll.messages || []);
    } catch {
      setMessages([]);
    }
    await ladeKontext(session.id);
  }, [setSearchParams, ladeKontext]);

  const pollDeepResult = useCallback((runId) => {
    const versuchStart = Date.now();
    const poll = async () => {
      if (Date.now() - versuchStart > 10 * 60 * 1000) return; // 10 Min Deckel
      try {
        const ergebnis = await betreuungAPI.zeoMittwaldRunResult(runId);
        if (['completed', 'failed', 'cancelled'].includes(ergebnis.status)) {
          if (ergebnis.antwort) {
            setMessages((prev) => [...prev, {
              id: `deep-${runId}`, rolle: 'assistent', text: ergebnis.antwort, timestamp: new Date().toISOString(),
              document_refs: ergebnis.document_refs || [], weitere_dokument_refs: ergebnis.weitere_dokument_refs || [], task_refs: ergebnis.task_refs || [],
            }]);
          }
          return;
        }
      } catch { return; }
      pollTimerRef.current = setTimeout(poll, 5000);
    };
    pollTimerRef.current = setTimeout(poll, 3000);
  }, []);

  const zeoNotiz = useCallback((text, extra = {}) => {
    setMessages((prev) => [...prev, { id: `aufg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, rolle: 'assistent', text, timestamp: new Date().toISOString(), ...extra }]);
  }, []);

  // Ausgewählte Aufgaben als Kontext: Zusammenführen/Erledigen öffnen die Aufgaben-2.0-Vorschau (dieselben Endpunkte wie /aufgaben, gespeichert wird erst
  // nach Bestätigung); Dokumentieren/„ja“ gehen an den normalen Chat (handleSend); jede andere Eingabe ist eine Analysefrage zu genau diesen Aufgaben
  // (POST /aufgaben/zeo-frage, ändert nichts).
  const aufgabenKontextSenden = useCallback(async (text, { alsNutzer = true } = {}) => {
    const ids = [...aufgabenAuswahl.keys()];
    if (alsNutzer) setMessages((prev) => [...prev, { id: `local-${Date.now()}`, rolle: 'user', text: `${text}\n— bezogen auf ${ids.length} ausgewählte Aufgabe(n)`, timestamp: new Date().toISOString() }]);
    const absicht = aufgabenKontextAbsicht(text);
    if (absicht === 'zusammenfuehren') {
      if (ids.length < 2) { zeoNotiz('Zum Zusammenführen bitte mindestens zwei Aufgaben auswählen. Es wurde nichts geändert.'); return; }
      zeoNotiz(`Vorschau zur Zusammenführung von ${ids.length} Aufgaben geöffnet – gespeichert wird erst nach Ihrer Bestätigung.`);
      setMergeIds(ids); return;
    }
    if (absicht === 'erledigen') {
      zeoNotiz(`Vorschau zum Erledigen von ${ids.length} Aufgabe(n) geöffnet – erledigt wird erst nach Ihrer Bestätigung; Fristen und Unteraufgaben bleiben unverändert.`);
      setErledigenIds(ids); return;
    }
    setLoading(true);
    try {
      // Frage + Antwort landen in der BESTEHENDEN Sitzung (assistent_messages); der Server prüft Sitzung und Klient vor dem Modellaufruf.
      const r = await betreuungAPI.zeoFrageAufgaben({ frage: text, modus: 'auswahl', aufgabe_ids: ids, session_id: activeSessionId || undefined });
      zeoNotiz(`Antwort zu ${(r.kontext || []).length || ids.length} ausgewählten Aufgabe(n) (Analyse, es wurde nichts geändert): ${r.kurzantwort || ''}`.trim()
        + (r.chat_gespeichert ? '' : '\n\n(Noch kein Chat vorhanden – Frage und Antwort wurden nicht im Verlauf gespeichert.)'), { aufgaben_frage: r });
      if (r.chat_gespeichert) ladeSessions();
    } catch (e) {
      zeoNotiz(`⚠️ ${e.message || 'ZEO konnte die ausgewählten Aufgaben nicht auswerten.'} Es wurde nichts geändert.`);
    } finally {
      setLoading(false);
    }
  }, [aufgabenAuswahl, zeoNotiz, activeSessionId, ladeSessions]);

  const handleSend = useCallback(async (text, interaktivKontext = {}) => {
    const { replyToMessageId, replyToBlockId, selectedDocumentIds, anhaenge } = interaktivKontext;
    if (aufgabenAuswahl.size && auswahlAlsKontext && !(anhaenge && anhaenge.length)) {
      if (aufgabenKontextAbsicht(text) !== 'chat') { await aufgabenKontextSenden(text); return; }
      // Dokumentieren/Nachfrage/„ja“ -> normaler ZEO-Chat (Schreibweg mit Vorschlag + Bestätigung); eine einzelne ausgewählte Aufgabe wird aktive Aufgabe
      if (aufgabenAuswahl.size === 1 && activeSessionId) {
        try { await betreuungAPI.zeoWorkspaceEntities(activeSessionId, { task_id: [...aufgabenAuswahl.keys()][0] }); } catch (e) { /* ohne aktive Aufgabe bestimmt ZEO das Ziel selbst oder fragt nach */ }
      }
    }
    const userMsg = {
      id: `local-${Date.now()}`, rolle: 'user', text, timestamp: new Date().toISOString(),
      reply_to_message_id: replyToMessageId || undefined,
      reply_to_block_id: replyToBlockId || undefined,
      selected_document_ids: (selectedDocumentIds && selectedDocumentIds.length) ? selectedDocumentIds : undefined,
      anhaenge: (anhaenge && anhaenge.length) ? anhaenge : undefined,
    };
    setMessages((prev) => [...prev, userMsg]);
    setReplyTarget(null);
    setSelectedDocuments([]);
    setLoading(true);
    try {
      const response = await betreuungAPI.chatAssistent({
        nachricht: text,
        session_id: activeSessionId,
        kontext_typ,
        kontext_id,
        modus: 'online',
        reply_to_message_id: replyToMessageId || undefined,
        reply_to_block_id: replyToBlockId || undefined,
        selected_document_ids: (selectedDocumentIds && selectedDocumentIds.length) ? selectedDocumentIds : undefined,
        attachment_ids: (anhaenge && anhaenge.length) ? anhaenge.map((a) => a.id) : undefined,
        zeo_workspace: true,   // Prod 09.10.2026: Anfrage stammt aus dem grossen ZEO (Server: Workspace-Verhalten, kein lokaler Fallback)
      });
      if (!activeSessionId && response.session_id) setActiveSessionId(response.session_id);
      // Sidebar IMMER nach einer Antwort auffrischen: der Chat-Titel entsteht serverseitig aus dem ersten Anliegen (sonst bliebe "Neuer Chat" stehen).
      ladeSessions();
      if (response.zeo_kontext) setKontext(response.zeo_kontext);
      if (response.session_id) ladeKontext(response.session_id);
      if (response.antwort) {
        setMessages((prev) => [...prev, {
          id: `resp-${Date.now()}`, rolle: 'assistent', text: response.antwort, timestamp: new Date().toISOString(),
          schreib_entwurf: response.schreib_entwurf || undefined,     // Vorschlag fuer die Korrespondenz (nichts gespeichert/versendet)
          schreib_message_id: response.message_id || undefined,
          document_refs: response.document_refs || [],
          weitere_dokument_refs: response.weitere_dokument_refs || [],
          task_refs: response.task_refs || [],
          ui_action: response.ui_action || undefined,
        }]);
      }
      // UI-Aktion OPEN_TASK: die KANONISCHE Olympus-Aufgabenmaske oeffnet (eigener Browser-Tab, der Chat bleibt hier erhalten).
      // Blockiert der Browser das Fenster, bleibt die Karte "Aufgabe oeffnen" als sicherer Weg bestehen.
      if (istOpenTask(response.ui_action)) {
        const geoeffnet = oeffneAufgabeFenster(response.ui_action.task_id);
        if (!geoeffnet) toast.info('Das Browserfenster wurde blockiert – bitte „Aufgabe öffnen“ in der Karte anklicken.');
      }
      if (response.modus === 'mittwald_deep_running' && response.run_id) {
        pollDeepResult(response.run_id);
      }
    } catch (e) {
      setMessages((prev) => [...prev, {
        id: `err-${Date.now()}`, rolle: 'assistent',
        text: '⚠️ ZEO konnte gerade nicht antworten. Bitte in Kürze erneut versuchen.',
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setLoading(false);
    }
  }, [activeSessionId, kontext_typ, kontext_id, ladeSessions, ladeKontext, pollDeepResult, aufgabenAuswahl, auswahlAlsKontext, aufgabenKontextSenden]);

  return (
    <div className="h-screen w-full flex" style={{ background: 'linear-gradient(180deg,#faf9ff 0%,#f5f3ff 100%)' }} data-testid="zeo-page">
      <ZeoSidebar
        open={sidebarOpen}
        sessions={sessions}
        activeSessionId={activeSessionId}
        onSelectSession={handleSelectSession}
        onNewChat={handleNewChat}
        klientNamenCache={klientNamenCache}
        aktiverKlientId={activeSessionId ? kontextIds.klient_id : null}
        onRename={handleRename}
        onSuche={handleChatSuche}
      />
      <div
        className={`flex-1 flex flex-col min-w-0 min-h-0 relative ${dateiUeberFenster ? 'ring-2 ring-inset ring-violet-400' : ''}`}
        data-testid="zeo-dropzone"
        onDragOver={(e) => { if (hatDateien(e.dataTransfer)) { e.preventDefault(); if (!dateiUeberFenster) setDateiUeberFenster(true); } }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDateiUeberFenster(false); }}
        onDrop={(e) => { if (!hatDateien(e.dataTransfer)) return; e.preventDefault(); setDateiUeberFenster(false); composerRef.current?.addFiles(dateienAusDrop(e.dataTransfer)); }}
      >
        {dateiUeberFenster && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-violet-50/70 pointer-events-none text-violet-700 text-sm font-medium" data-testid="zeo-drop-overlay">
            Dateien hier ablegen, um sie an die Nachricht anzuhängen
          </div>
        )}
        <ZeoScopeHeader
          scope={scope}
          klientName={aktuellerKlientName}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen((v) => !v)}
        />
        <ZeoContextBar
          kontext={activeSessionId || scope !== 'client' ? kontext : { ...leererKontext(), klient: aktuellerKlientName || 'Klient wird geladen…' }}
          onKlientWechseln={() => setDialog('klient')}
          onAufgabeWechseln={() => setDialog('aufgabe')}
          onNeueAufgabe={() => setDialog('neue-aufgabe')}
        />
        <ZeoConversation
          messages={messages} sessionId={activeSessionId} loading={loading} scope={scope} klientName={aktuellerKlientName}
          onAktivesDokument={handleAktivesDokument}
          onAufgabeOeffnen={handleAufgabeOeffnen}
          aktuelleKlientId={scope === 'client' ? kontext_id : null}
          onReply={handleReply}
          selectedDocumentIds={selectedDocuments.map((d) => d.id)}
          onToggleDocument={handleToggleDocument}
          aufgabenAuswahl={aufgabenAuswahl} onAufgabenAuswahl={setAufgabenAuswahl} aufgabenStand={aufgabenStand}
          onAufgabenZusammenfuehren={(liste) => { setAufgabenAuswahl(nur(liste)); setMergeIds(liste.map((a) => a.id)); }}
          onAufgabenErledigen={(liste) => { setAufgabenAuswahl(nur(liste)); setErledigenIds(liste.map((a) => a.id)); }}
        />
        {aufgabenAuswahl.size > 0 && (
          <div className="mx-auto w-full max-w-3xl px-4" data-testid="zeo-aufgaben-aktionsleiste">
            <div className="flex flex-wrap items-center gap-2 bg-slate-900 text-white rounded-xl px-3 py-2 text-xs">
              <span className="font-medium" data-testid="zeo-aufgaben-anzahl">{aufgabenAuswahl.size} Aufgabe(n) ausgewählt</span>
              <label className="flex items-center gap-1 text-slate-300">
                <input type="checkbox" checked={auswahlAlsKontext} onChange={(e) => setAuswahlAlsKontext(e.target.checked)} data-testid="zeo-aufgaben-als-kontext" />
                als Kontext für die ZEO-Eingabe
              </label>
              <span className="flex-1" />
              <button type="button" className="px-2 py-1 rounded hover:bg-slate-800 text-violet-300" disabled={loading} data-testid="zeo-aufgaben-analysieren"
                onClick={() => aufgabenKontextSenden('Analysiere diese Aufgaben: Was gehört zusammen, was ist doppelt, was ist noch offen?')}>
                Mit ZEO analysieren
              </button>
              <button type="button" className="px-2 py-1 rounded hover:bg-slate-800 text-violet-300 disabled:opacity-40" disabled={aufgabenAuswahl.size < 2}
                onClick={() => setMergeIds([...aufgabenAuswahl.keys()])} data-testid="zeo-aufgaben-zusammenfuehren">Intelligent zusammenführen</button>
              <button type="button" className="px-2 py-1 rounded hover:bg-slate-800 text-green-400" onClick={() => setErledigenIds([...aufgabenAuswahl.keys()])}
                data-testid="zeo-aufgaben-erledigen">Als erledigt markieren</button>
              <button type="button" className="px-2 py-1 rounded hover:bg-slate-800 text-slate-300" onClick={() => setAufgabenAuswahl(new Map())}
                data-testid="zeo-aufgaben-aufheben">Auswahl aufheben</button>
            </div>
          </div>
        )}
        <ZeoComposer
          ref={composerRef}
          onSend={handleSend} loading={loading}
          replyTarget={replyTarget} onClearReply={() => setReplyTarget(null)}
          selectedDocuments={selectedDocuments} onRemoveSelectedDocument={handleRemoveSelectedDocument}
        />
      </div>
      <ZusammenfuehrenDialog open={Boolean(mergeIds)} ids={mergeIds || []} onClose={() => setMergeIds(null)}
        onDone={(r) => { setMergeIds(null); setAufgabenAuswahl(new Map()); setAufgabenStand((x) => x + 1); if ((r?.ok || r?.bereits_ausgefuehrt) && r.haupt_aufgabe_id && activeSessionId) betreuungAPI.zeoWorkspaceEntities(activeSessionId, { task_id: r.haupt_aufgabe_id }).then(() => setKontextIds((k) => ({ ...k, task_id: r.haupt_aufgabe_id }))).catch(() => {}); if (r?.ok || r?.bereits_ausgefuehrt) zeoNotiz(r.bereits_ausgefuehrt ? 'Diese Zusammenführung war bereits gespeichert – nichts doppelt angelegt.' : `${r.unteraufgaben} Aufgabe(n) zusammengeführt (aus der Datenbank zurückgelesen).`); }} />
      <SammelErledigenDialog open={Boolean(erledigenIds)} ids={erledigenIds || []} onClose={() => setErledigenIds(null)}
        onDone={(r) => { setErledigenIds(null); setAufgabenAuswahl(new Map()); setAufgabenStand((x) => x + 1); zeoNotiz(`${r.vollstaendig ? 'Erledigt' : 'Teilweise erledigt'}: ${r.erledigt} geändert, ${r.unveraendert} unverändert, ${r.fehler} nicht ausgeführt (zurückgelesen).`); }} />
      {dialog && (
        <ZeoWorkspaceDialog
          mode={dialog} klientId={kontextIds.klient_id} sessionId={activeSessionId}
          aktuelleAufgabeId={kontextIds.task_id || ''} aktuellerVorgangId={kontextIds.vorgang_id || ''}
          onClose={() => setDialog(null)} onStartChat={handleStartChat}
          onAufgabeGewaehlt={handleAufgabeGewaehlt} onAufgabeAngelegt={handleAufgabeAngelegt}
        />
      )}
    </div>
  );
}
